import { Box } from '@chakra-ui/react';
import { ICON_GRADIENT_SVG_ID, SPINNER_GRADIENT_SVG_ID } from '../../theme/iconGradient';

/**
 * The <linearGradient> definitions that `iconGradientSvgSx` paints inline-SVG
 * icons with. Rendered ONCE near the app root; an SVG paint server is
 * document-scoped, so every BowerIcon on the page references these same two
 * IDs rather than carrying its own copy.
 *
 * The host <svg> is zero-sized and removed from the a11y tree — it draws
 * nothing itself and exists only to own the defs.
 *
 * GEOMETRY. `gradientUnits` is left at its default (`objectBoundingBox`), so
 * one definition rescales to every icon it paints regardless of pixel size.
 *
 * The vectors are what make these match their CSS counterparts exactly:
 *
 *   warm  `135deg, s1 0%, s2 45%, s3 100%` maps straight onto the box
 *         diagonal (0,0)->(1,1) with the stops at their stated offsets.
 *
 *   cool  `135deg, s3 -40%, s1 30%, s2 100%` has a NEGATIVE first offset,
 *         which SVG cannot express — `offset` clamps to [0,1]. Clamping it
 *         would drag the warm stop back onto the glyph and reintroduce the
 *         lollipop the cool variant exists to avoid. So the gradient LINE is
 *         extended instead: starting the vector at (-0.4,-0.4) makes the span
 *         140% of the diagonal, and the three CSS offsets renormalise onto it
 *         as 0 / 0.5 / 1 — ((30 - -40) / 140 = 0.5). Same pixels, legal SVG.
 *
 * Stop colors are the shared `icon.gradientStop*` tokens, so these follow the
 * color mode with no JS and no second source of truth.
 *
 * ONE OF THE THREE IS NOT AN ICON GRADIENT. `BowerSpinner`'s comet is a paint
 * server for one component, not a treatment any glyph can opt into, and it is
 * here for the mechanical reason rather than the conceptual one: an SVG paint
 * reference is document-scoped, so every id a Bower document resolves has to be
 * declared in exactly one place or two of them eventually collide.
 */
export function BrandIconGradientDefs() {
  return (
    <Box
      as="svg"
      aria-hidden
      focusable="false"
      position="absolute"
      width="0"
      height="0"
      overflow="hidden"
    >
      <defs>
        <linearGradient id={ICON_GRADIENT_SVG_ID.warm} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--chakra-colors-icon-gradientStop1)" />
          <stop offset="0.45" stopColor="var(--chakra-colors-icon-gradientStop2)" />
          <stop offset="1" stopColor="var(--chakra-colors-icon-gradientStop3)" />
        </linearGradient>
        <linearGradient id={ICON_GRADIENT_SVG_ID.cool} x1="-0.4" y1="-0.4" x2="1" y2="1">
          <stop offset="0" stopColor="var(--chakra-colors-icon-gradientStop3)" />
          <stop offset="0.5" stopColor="var(--chakra-colors-icon-gradientStop1)" />
          <stop offset="1" stopColor="var(--chakra-colors-icon-gradientStop2)" />
        </linearGradient>
        {/*
          The spinner's comet. Same three ROLES as the warm icon ramp, so in
          dark mode the arc travels bluebird -> twilight -> sunset. Light mode
          runs bluebird -> twilight at the 700 step (composited at these
          opacities the 500 step failed WCAG 1.4.11 at 1.43:1) into a Sunset
          500 head, kept for the brand although it is under 3:1. See
          `semanticTokens.gradient.ts` and the recorded shortfalls.

          It also carries OPACITY as well as colour, so the tail thins out
          instead of stopping dead. The 0.18 floor is not decoration: at a true
          zero the faint end goes sub-pixel at 12-16px and the arc stops reading
          as a ring at the size it is used most.
        */}
        <linearGradient id={SPINNER_GRADIENT_SVG_ID} x1="0.1" y1="1" x2="0.9" y2="0">
          <stop
            offset="0"
            stopColor="var(--chakra-colors-spinner-gradientStop1)"
            stopOpacity="0.18"
          />
          <stop
            offset="0.45"
            stopColor="var(--chakra-colors-spinner-gradientStop2)"
            stopOpacity="0.7"
          />
          <stop offset="1" stopColor="var(--chakra-colors-spinner-gradientStop3)" stopOpacity="1" />
        </linearGradient>
      </defs>
    </Box>
  );
}
